import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounce, timer } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { Option } from '@interfaces/common/option';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import {
  AccountingDocumentListItem,
  AccountingDocumentStatus,
  ExtractionOutcome,
} from '@interfaces/accounting-data-standardization/responses/accounting-document-list-item.response';
import {
  AccountingDocumentSortDirection,
  AccountingDocumentSortField,
  AccountingDocumentTypeFilter,
  DEFAULT_DOCUMENT_SORT,
} from '@interfaces/accounting-data-standardization/requests/accounting-document-list-filter.request';
import {
  DOC_ICON_CLASS,
  DOC_ICON_LETTER,
  DOC_TYPE_LABEL,
  resolveDocIconKind,
} from '@features/dashboard/accounting-data-standardization/utils/document-icon.util';
import { AccountingProgressPercentComponent } from '@shared/components/accounting-progress-percent/accounting-progress-percent.component';
import {
  isProgressActive,
  progressLabel,
  progressPercent,
} from '@shared/utils/accounting-progress.util';
import { DocumentAnalysisSituationComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/document-analysis-situation/document-analysis-situation.component';
import { DocumentOpenRequest } from '@src/app/features/dashboard/accounting-data-standardization/components/years-without-document-banner/years-without-document-banner.component';

type ListLayout = 'tabela' | 'cards';
/** Debounce da busca por texto: sem isso, cada tecla digitada dispara uma requisição ao servidor. */
const SEARCH_DEBOUNCE_MS = 350;
/** Opções de linhas por página oferecidas no rodapé. Todas dentro do teto do backend (200). */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
/** Quantidade máxima de botões numerados exibidos de uma vez na paginação. */
const MAX_PAGE_BUTTONS = 5;

/**
 * Rótulos pt-BR dos status derivados. Fonte única para o chip da linha e para as opções do filtro de
 * status — duplicar os rótulos faria o filtro dizer "Extraido" enquanto a coluna diz "Extraído".
 */
const STATUS_LABEL: Record<AccountingDocumentStatus, string> = {
  Erro: 'Erro',
  Processando: 'Processando',
  NaoExtraido: 'Não extraído',
  Extraido: 'Extraído',
  Invalidado: 'Invalidado',
  Validado: 'Validado',
};

/** Ordem dos status no filtro: do estágio mais adiantado do fluxo para o mais atrasado. */
const STATUS_FILTER_ORDER: AccountingDocumentStatus[] = [
  'Validado',
  'Invalidado',
  'Extraido',
  'NaoExtraido',
  'Processando',
  'Erro',
];

@Component({
  selector: 'app-document-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    AccountingProgressPercentComponent,
    DocumentAnalysisSituationComponent,
  ],
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentListComponent {
  private readonly dateFormatterService = inject(DateFormatterService);
  private readonly destroyRef = inject(DestroyRef);

  documents = input<AccountingDocumentListItem[]>([]);
  isLoading = input<boolean>(false);
  isReprocessing = input<boolean>(false);
  search = input<string>('');
  filterType = input<AccountingDocumentTypeFilter>('Todos');
  /** Status selecionados no filtro (multisseleção). Vazio = todos. */
  filterStatuses = input<AccountingDocumentStatus[]>([]);
  /** Anos selecionados no filtro (multisseleção). Vazio = todos. */
  filterYears = input<number[]>([]);
  availableYears = input<number[]>([]);
  page = input<number>(1);
  pages = input<number>(0);
  total = input<number>(0);
  /** Linhas por página em vigor, escolhidas pelo usuário no rodapé. */
  pageSize = input<number>(PAGE_SIZE_OPTIONS[0]);
  /** Empresas selecionadas no filtro da página. Vazio = todas. */
  selectedCompanyIds = input<string[]>([]);
  /** Coluna da ordenação em vigor (feita no servidor). */
  sortField = input<AccountingDocumentSortField>(DEFAULT_DOCUMENT_SORT.field);
  /** Direção da ordenação em vigor. */
  sortDirection = input<AccountingDocumentSortDirection>(DEFAULT_DOCUMENT_SORT.direction);

  documentSelected = output<AccountingDocumentListItem>();
  /**
   * Abrir outro documento pelo id (link "já vale por X" da coluna "Situação na análise", card 3700). O outro documento
   * pode não estar na página atual, então quem abre é o link compartilhável (`doc` + `docCompanyId`).
   */
  documentOpenRequested = output<DocumentOpenRequest>();
  /** Clique num cabeçalho ordenável; quem decide a direção é o state service (mesma coluna inverte). */
  sortChanged = output<AccountingDocumentSortField>();
  reprocessSelected = output<string[]>();
  searchChanged = output<string>();
  typeChanged = output<AccountingDocumentTypeFilter>();
  statusesChanged = output<AccountingDocumentStatus[]>();
  yearsChanged = output<number[]>();
  pageChanged = output<number>();
  pageSizeChanged = output<number>();

  readonly layout = signal<ListLayout>('tabela');

  /**
   * A coluna "Empresa" só é escondida quando exatamente uma empresa está selecionada — aí ela seria uma
   * coluna com o mesmo valor repetido. A regra olha a seleção, e não o conteúdo da página, para a coluna
   * não aparecer e sumir conforme o usuário navega entre páginas.
   */
  readonly showCompanyColumn = computed<boolean>(() => this.selectedCompanyIds().length !== 1);

  readonly countLabel = computed<string>(() => {
    const count = this.total();
    return `${count} ${count === 1 ? 'documento' : 'documentos'}`;
  });

  /**
   * Opções do filtro de ano. Sem sentinela "Todos os anos": o filtro é multisseleção e nenhuma opção
   * marcada já significa todos — a sentinela seria um segundo jeito de dizer a mesma coisa, e marcá-la
   * junto de um ano específico não teria significado.
   */
  readonly yearOptions = computed<Option[]>(() =>
    this.availableYears().map(year => ({ label: String(year), value: String(year) }))
  );

  /** O `app-select` múltiplo trabalha com array de `value`s (string), daí a conversão do ano. */
  readonly yearSelectValues = computed<string[]>(() =>
    this.filterYears().map(year => String(year))
  );

  /** Opções do filtro de status. Fixas — os status possíveis não dependem do que está carregado. */
  readonly statusOptions: Option[] = STATUS_FILTER_ORDER.map(status => ({
    label: STATUS_LABEL[status],
    value: status,
  }));

  /** Opções do seletor de linhas por página. Fixas — não dependem de estado. */
  readonly pageSizeOptions: Option[] = PAGE_SIZE_OPTIONS.map(size => ({
    label: String(size),
    value: String(size),
  }));

  /** Valor do seletor de linhas por página (o `app-select` trabalha com string). */
  readonly pageSizeValue = computed<string>(() => String(this.pageSize()));

  /**
   * Janela deslizante de páginas numeradas, de no máximo {@link MAX_PAGE_BUTTONS} botões. Sem a janela,
   * uma consulta com 40 páginas renderizaria 40 botões e estouraria o rodapé. A janela é sempre
   * preenchida por completo quando há páginas suficientes (não encolhe perto das bordas): nas primeiras
   * páginas ela cola no início, nas últimas cola no fim, e no meio fica centrada na página atual — é o
   * comportamento que mantém a largura do rodapé estável enquanto o usuário navega.
   */
  readonly visiblePages = computed<number[]>(() => {
    const totalPages = this.pages();
    if (totalPages < 1) return [];

    const windowSize = Math.min(MAX_PAGE_BUTTONS, totalPages);
    const half = Math.floor(windowSize / 2);
    const start = Math.min(Math.max(this.page() - half, 1), totalPages - windowSize + 1);

    return Array.from({ length: windowSize }, (_, index) => start + index);
  });

  /**
   * Valor exibido no campo de busca. Fica desacoplado do input `search()` (que só reflete o termo já
   * confirmado no servidor, com debounce) para o usuário conseguir digitar sem que o round-trip
   * reescreva o campo no meio da digitação — se o `<input>` ficasse amarrado direto a `search()`, cada
   * novo ciclo de change detection reescreveria o valor exibido para o último termo já resolvido pelo
   * servidor, apagando os caracteres que o usuário ainda está digitando. `linkedSignal` porque ainda
   * precisa acompanhar `search()` quando ele muda por fora do campo (troca de empresa reseta a busca).
   */
  readonly searchTerm = linkedSignal<string>(() => this.search());

  private readonly searchInput$ = new Subject<string>();

  /**
   * Assinatura estável do CONJUNTO de documentos da página atual — não da sequência em que chegaram.
   * Ids são ordenados antes de concatenar de propósito: o backend pagina uma listagem ordenada por
   * data de modificação (`LastModifiedAt`), e essa data muda sozinha sempre que o pipeline altera o
   * status de um documento (ex.: "Processando" → "Extraído") — que é justamente o evento que o
   * polling de 5s existe para capturar. Quando isso acontece, o item sobe na ordenação e a página
   * inteira se desloca, mesmo contendo o mesmo conjunto de ids de antes. Um fingerprint sensível à
   * ordem trocaria a cada tick de polling com documento em processamento e derrubaria a seleção do
   * usuário a cada 5 segundos — exatamente o cenário mais comum de uso desta tela. Por isso comparamos
   * o conjunto (ids ordenados), não a sequência.
   */
  private readonly documentIdsFingerprint = computed<string>(() =>
    this.documents()
      .map(doc => doc.id)
      .sort()
      .join('|')
  );

  /**
   * Seleção de re-extração. Os checkboxes ficam amarrados visualmente às linhas exibidas na página
   * atual — se o conteúdo da lista mudar (nova página, busca, filtro de tipo/ano) sem limpar a seleção,
   * o botão "Extrair selecionados" continua ativo mostrando uma contagem de ids que não aparecem mais
   * na tela, e o clique não envia nada (o state service tenta casar os ids selecionados contra a página
   * atual, não acha nenhum, e mostra "Nenhum documento elegível"). Por isso a seleção é um `linkedSignal`
   * amarrado a `documentIdsFingerprint`: ela reseta para um Set vazio sempre que o CONTEÚDO muda, mas
   * continua gravável normalmente (toggle/clear) enquanto o conteúdo não mudar — o tick de polling que
   * devolve a mesma página com os mesmos ids não derruba a seleção do usuário a cada 5 segundos.
   */
  private readonly selectedIds = linkedSignal<string, Set<string>>({
    source: this.documentIdsFingerprint,
    computation: () => new Set<string>(),
  });

  /** Quantidade de documentos atualmente selecionados para re-extração. */
  readonly selectedCount = computed<number>(() => this.selectedIds().size);

  constructor() {
    // Busca com debounce: sem isso, cada tecla dispara uma requisição e a lista pisca (troca para o
    // spinner de loading) a cada caractere digitado. Esvaziar o campo (limpar a busca) emite na hora,
    // sem esperar o debounce — o usuário espera ver a lista completa de volta assim que apaga o texto.
    this.searchInput$
      .pipe(
        debounce(value => timer(value === '' ? 0 : SEARCH_DEBOUNCE_MS)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => this.searchChanged.emit(value));
  }

  setLayout(layout: ListLayout): void {
    this.layout.set(layout);
  }

  setFilterType(type: AccountingDocumentTypeFilter): void {
    this.typeChanged.emit(type);
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.searchInput$.next(value);
  }

  /**
   * Troca o filtro de status (multisseleção). Os `value`s do `app-select` já são os próprios literais de
   * status. A guarda de igualdade evita recarregar a lista quando o `ngModel` reemite a mesma seleção —
   * cada recarga volta para a página 1 e cancela a leitura de quem estava navegando.
   */
  onStatusFilterChange(values: string[] | null): void {
    const statuses = (values ?? []) as AccountingDocumentStatus[];
    if (this.sameSelection(statuses, this.filterStatuses())) return;
    this.statusesChanged.emit(statuses);
  }

  /** Troca o filtro de ano (multisseleção). Vazio = todos os anos. */
  onYearFilterChange(values: string[] | null): void {
    const years = (values ?? []).map(value => Number(value)).filter(year => Number.isFinite(year));
    if (this.sameSelection(years, this.filterYears())) return;
    this.yearsChanged.emit(years);
  }

  /** Compara duas seleções ignorando a ordem — o multiselect não garante ordem estável. */
  private sameSelection<T>(a: readonly T[], b: readonly T[]): boolean {
    if (a.length !== b.length) return false;
    const current = new Set(b);
    return a.every(item => current.has(item));
  }

  /** Navega para uma página específica, respeitando os limites e ignorando clique na página atual. */
  goToPage(page: number): void {
    if (page < 1 || page > this.pages() || page === this.page()) return;
    this.pageChanged.emit(page);
  }

  /** Troca as linhas por página. Quem volta para a página 1 é o state service (ver `setPageSize`). */
  onPageSizeChange(value: string): void {
    const size = Number(value);
    if (!Number.isFinite(size) || size < 1 || size === this.pageSize()) return;
    this.pageSizeChanged.emit(size);
  }

  onSelect(item: AccountingDocumentListItem): void {
    this.documentSelected.emit(item);
  }

  /** Link do documento que vale no lugar deste: mesma empresa, outro id. */
  onOpenCovered(doc: AccountingDocumentListItem, extractionId: string): void {
    this.documentOpenRequested.emit({ companyId: doc.companyId, extractionId });
  }

  /** Linha com período já coberto por outro documento validado ganha fundo âmbar claro (protótipo, opção A). */
  isPeriodCovered(doc: AccountingDocumentListItem): boolean {
    return doc.analysisSituation?.kind === 'PeriodCovered';
  }

  /**
   * Quem decide se a linha pode ser selecionada para re-extração é o **backend** (`canReprocess`), pela
   * mesma regra que o endpoint de reprocesso aplica. Isso inclui documento preso em `Processando` há mais
   * de 30 minutos — órfão de um worker que morreu, que antes não ganhava checkbox nenhum e por isso não
   * tinha como ser recuperado pela tela (card #2822).
   *
   * O fallback para a regra antiga cobre resposta de backend anterior a este campo; assim que o campo
   * chega, ele manda.
   */
  selectable(doc: AccountingDocumentListItem): boolean {
    return doc.canReprocess ?? (doc.status === 'NaoExtraido' || doc.status === 'Erro');
  }

  isSelected(doc: AccountingDocumentListItem): boolean {
    return this.selectedIds().has(doc.id);
  }

  toggleSelect(doc: AccountingDocumentListItem, event?: Event): void {
    event?.stopPropagation();
    if (!this.selectable(doc)) return;
    const next = new Set(this.selectedIds());
    if (next.has(doc.id)) {
      next.delete(doc.id);
    } else {
      next.add(doc.id);
    }
    this.selectedIds.set(next);
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  onReprocessSelected(): void {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0) return;
    this.reprocessSelected.emit(ids);
    this.clearSelection();
  }

  typeLabel(doc: AccountingDocumentListItem): string {
    return DOC_TYPE_LABEL[resolveDocIconKind(doc)];
  }

  iconLetter(doc: AccountingDocumentListItem): string {
    return DOC_ICON_LETTER[resolveDocIconKind(doc)];
  }

  iconClass(doc: AccountingDocumentListItem): string {
    return DOC_ICON_CLASS[resolveDocIconKind(doc)];
  }

  /** Nome da empresa do documento, com traço quando o backend não resolveu (empresa fora do escopo). */
  companyLabel(doc: AccountingDocumentListItem): string {
    return doc.companyName || '—';
  }

  /**
   * Rótulo do chip de status. O `?? status` cobre o caso de o backend devolver um status que este front
   * ainda não conhece (front novo com API antiga, ou vice-versa): sem ele o chip renderizava **vazio** —
   * foi o que aconteceu ao renomear "EmRevisao" para "Invalidado" com a API local rodando o binário
   * anterior. String crua na tela é feia, mas diz o que está errado; chip em branco não diz nada.
   */
  statusLabel(status: AccountingDocumentStatus): string {
    return STATUS_LABEL[status] ?? status;
  }

  statusClass(status: AccountingDocumentStatus): string {
    const map: Record<AccountingDocumentStatus, string> = {
      Erro: 'status-erro',
      Processando: 'status-processando',
      NaoExtraido: 'status-nao-extraido',
      Extraido: 'status-extraido',
      Invalidado: 'status-invalidado',
      Validado: 'status-validado',
    };
    return map[status];
  }

  /**
   * Selo da etapa AUTOMÁTICA de De/Para, exibido ao lado do chip de status. Nulo quando não há nada a
   * dizer: etapa concluída, documento de Faturamento (não tem De/Para) ou documento anterior à
   * funcionalidade — nesses casos a listagem OMITE o selo, em vez de afirmar um estado não apurado.
   *
   * Vive ao LADO do status, e não dentro dele, porque o filtro de status casa pelo nome do membro do enum
   * do backend: um status novo faria o documento nesse estado sumir de quem filtra por "Extraído", sem
   * erro nenhum. Ver bug #2929.
   */
  deParaBadge(
    doc: AccountingDocumentListItem
  ): { label: string; cssClass: string; tooltip: string } | null {
    switch (doc.deParaState) {
      case 'Enqueued':
        return {
          label: 'De/Para em andamento',
          cssClass: 'depara-badge--pending',
          tooltip:
            'A sugestão automática de De/Para ainda está rodando. Até terminar, as linhas do documento ' +
            'aparecem como "Pendente".',
        };
      case 'NoSuggestions':
        return {
          label: 'De/Para sem sugestão',
          cssClass: 'depara-badge--none',
          tooltip:
            'A sugestão automática rodou e não produziu nenhum vínculo. Abra o documento e use ' +
            '"Sugerir De/Para" ou classifique manualmente.',
        };
      case 'Incomplete':
        return {
          label: 'De/Para incompleto',
          cssClass: 'depara-badge--none',
          tooltip:
            'A sugestão automática terminou, mas o agente não devolveu conta para algumas linhas. Abra o ' +
            'documento e use o filtro "Pendentes" do De/Para para classificá-las.',
        };
      case 'Failed':
        return {
          label: 'De/Para falhou',
          cssClass: 'depara-badge--failed',
          tooltip:
            'A sugestão automática de De/Para falhou. A extração está concluída e os valores estão ' +
            'salvos — abra o documento e use "Sugerir De/Para".',
        };
      default:
        return null;
    }
  }

  /**
   * Marco atual da esteira contábil ("Execução 6 de 40", "De/Para: 120 de 225 linhas"), só enquanto ela anda (fila,
   * leitura ou De/Para). O texto vem pronto do backend (`progress.label`); sem o campo (backend anterior) ou com a
   * esteira parada, `null` e a coluna fica como era. A lista já se atualiza a cada 5 s enquanto há documento em
   * andamento, então o texto acompanha sozinho.
   */
  progressText(doc: AccountingDocumentListItem): string | null {
    return isProgressActive(doc.progress) ? progressLabel(doc.progress) || null : null;
  }

  /**
   * O backend mandou a porcentagem estimada: a coluna mostra "lendo · 45%" com a barra fina
   * (`app-accounting-progress-percent`, o mesmo do selo do Data Room e do aviso da Análise Econômica), e o marco real vai
   * para o tooltip. Sem ela, fica o marco como antes.
   */
  hasProgressPercent(doc: AccountingDocumentListItem): boolean {
    return isProgressActive(doc.progress) && progressPercent(doc.progress) != null;
  }

  /** Tooltip do marco atual: o texto inteiro e o complemento, quando houver. */
  progressTooltip(doc: AccountingDocumentListItem): string {
    const detail = doc.progress?.detail?.trim();
    return [this.progressText(doc), detail, 'Abra o documento para ver a linha do tempo.']
      .filter(Boolean)
      .join(' · ');
  }

  /**
   * Texto do tooltip do chip de status: o motivo real reportado pelo agente na última execução.
   * Vazio quando não há diagnóstico (documento anterior à funcionalidade) ou quando a execução correu
   * limpa — nesses casos o chip não tem nada a explicar.
   */
  statusTooltip(doc: AccountingDocumentListItem): string {
    return doc.lastExtractionSummary ?? '';
  }

  /**
   * Indica se a linha deve exibir o ícone de diagnóstico. Só quando há execução registrada E ela não foi
   * um sucesso limpo: marcar documento bem-sucedido com ícone transformaria o indicador em ruído, e
   * marcar documento sem diagnóstico afirmaria algo que nunca foi apurado.
   */
  showDiagnosticIcon(doc: AccountingDocumentListItem): boolean {
    const outcome = doc.lastExtractionOutcome;
    return !!outcome && outcome !== 'Success';
  }

  /** Classe do ícone de diagnóstico, por desfecho da última execução. */
  diagnosticIconClass(doc: AccountingDocumentListItem): string {
    const map: Record<ExtractionOutcome, string> = {
      Success: '',
      SuccessWithWarnings: 'diag-warning',
      GuardRejected: 'diag-warning',
      NoData: 'diag-nodata',
      Failed: 'diag-failed',
    };
    return doc.lastExtractionOutcome ? map[doc.lastExtractionOutcome] : '';
  }

  /**
   * Glifo PrimeIcons do ícone de diagnóstico, por desfecho — falha, aviso e "sem dados" têm formas
   * distintas, e não só cores distintas, para o indicador não depender de percepção de cor.
   *
   * Até 18/08/2026 isto devolvia as **letras** "!" e "i" desenhadas dentro de um círculo colorido, o que
   * lia como texto solto ao lado do chip de status, não como ícone. Os três glifos escolhidos já são
   * circulares, então o fundo redondo saiu junto (círculo dentro de círculo).
   */
  diagnosticIcon(doc: AccountingDocumentListItem): string {
    const map: Record<ExtractionOutcome, string> = {
      Success: '',
      SuccessWithWarnings: 'pi-exclamation-circle',
      GuardRejected: 'pi-exclamation-circle',
      NoData: 'pi-info-circle',
      Failed: 'pi-times-circle',
    };
    return doc.lastExtractionOutcome ? map[doc.lastExtractionOutcome] : '';
  }

  /**
   * Rótulo acessível do ícone de diagnóstico. Sem ele o leitor de tela anunciaria só a letra "i"/"!",
   * que é o texto visual do símbolo e não diz nada. Inclui o motivo quando existe, para o usuário de
   * leitor de tela receber a mesma informação que o tooltip dá ao mouse.
   */
  diagnosticIconAriaLabel(doc: AccountingDocumentListItem): string {
    const reason = doc.lastExtractionSummary;
    return reason
      ? `Diagnóstico da extração: ${reason}`
      : 'Diagnóstico da extração disponível no documento';
  }

  /**
   * Data **e hora** do processamento, no fuso e nos formatos do perfil do usuário. Até 17/09/2026 isto
   * chamava `formatDateInstant(iso, true)`, cujo `truncateTime` descartava a hora antes de formatar:
   * como a extração do onboarding roda em lote, a coluna inteira exibia a mesma data e deixava de
   * discriminar qualquer coisa — não dava para saber a ordem de processamento nem qual foi o
   * reprocessamento mais recente. Trocar o flag para `false` não resolveria: a formatação final usaria
   * `dateFormat`, um padrão só de data.
   *
   * Usa a variante **sem segundos**: para "quando o documento foi processado", o segundo é ruído — o
   * que a coluna precisa dar é a ordem e o horário. `formatDateTimeMinutesInstant` corta `:ss`/`.SSS`
   * do formato do próprio usuário, então a escolha 12h/24h e o fuso do perfil continuam valendo.
   *
   * Serve a tabela e os cards — os dois templates chamam este método, então as duas layouts não
   * divergem.
   */
  formatDateTime(iso: string): string {
    return this.dateFormatterService.formatDateTimeMinutesInstant(iso);
  }

  /** Clique no cabeçalho de uma coluna ordenável. */
  onSort(field: AccountingDocumentSortField): void {
    this.sortChanged.emit(field);
  }

  /** Ícone do cabeçalho: seta da direção na coluna ativa, ícone neutro nas demais. */
  sortIcon(field: AccountingDocumentSortField): string {
    if (this.sortField() !== field) return 'pi-sort-alt';
    return this.sortDirection() === 'Asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down';
  }

  /** Valor de `aria-sort` do cabeçalho, para leitor de tela anunciar a ordem em vigor. */
  ariaSort(field: AccountingDocumentSortField): 'ascending' | 'descending' | 'none' {
    if (this.sortField() !== field) return 'none';
    return this.sortDirection() === 'Asc' ? 'ascending' : 'descending';
  }

  /**
   * Quem enviou o documento. Registro antigo sem autor ou usuário não encontrado mostra "—", nunca o id
   * cru (o backend já devolve o nome nulo nesses casos).
   */
  submittedByLabel(doc: AccountingDocumentListItem): string {
    return doc.submittedByName?.trim() || '—';
  }

  /** Quando o documento foi enviado; registro antigo sem data de criação mostra "—". */
  submittedAtLabel(doc: AccountingDocumentListItem): string {
    return doc.submittedAt ? this.formatDateTime(doc.submittedAt) : '—';
  }
}
